import React, { useMemo, useState } from 'react';
import { Button, DataGrid } from '../../layout';

const CLIENTS_URL = `${process.env.REACT_APP_API_URL}/api/clients-datatable`;
const CLIENTS_INITIAL_SORT = { key: 'CompanyName', dir: 'asc' };

const Pill = ({ className, children }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${className}`}>{children}</span>
);

const getClientColumns = ({ showActions, canView, canEdit, canDelete, onView, onEdit, onDelete }) => {
    const columns = [
        { key: 'CustSupp', header: 'Codice', sortable: true, cellClassName: 'px-4 text-sm font-mono font-semibold text-gray-800', render: (c) => c.CustSupp },
        { key: 'CompanyName', header: 'Ragione Sociale', sortable: true, cellClassName: 'px-4 text-sm font-semibold text-gray-800', render: (c) => c.CompanyName },
        { key: 'TaxIdNumber', header: 'P.IVA', sortable: true, cellClassName: 'px-4 text-sm text-gray-600', render: (c) => c.TaxIdNumber },
        { key: 'NameSalesPerson', header: 'Agente', sortable: true, cellClassName: 'px-4 text-sm text-gray-600', render: (c) => c.NameSalesPerson },
        {
            key: 'CustomerClassification',
            header: 'Tipo',
            sortable: true,
            render: (c) => (c.CustomerClassification === '001'
                ? <Pill className="bg-green-100 text-green-700">Partner</Pill>
                : <Pill className="bg-blue-100 text-blue-700">Cliente</Pill>)
        },
        {
            key: 'is_active',
            header: 'Stato',
            sortable: false,
            render: (c) => (Number(c.is_active) === 1 || c.is_active === null || c.is_active === undefined
                ? <Pill className="bg-green-100 text-green-700">Attivo</Pill>
                : <Pill className="bg-gray-200 text-gray-600">Disabilitato</Pill>)
        }
    ];

    if (showActions) {
        columns.push({
            key: 'actions',
            header: 'Azioni',
            sortable: false,
            align: 'center',
            render: (c) => (
                <div className="flex items-center justify-center gap-2">
                    {canView && (
                        <button
                            onClick={() => onView && onView(c)}
                            className="px-3 py-1.5 border border-gray-400 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50 transition-colors"
                        >
                            Visualizza
                        </button>
                    )}
                    {canEdit && (
                        <button
                            onClick={() => onEdit(c)}
                            className="px-3 py-1.5 border border-blue-500 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-50 transition-colors"
                        >
                            Modifica
                        </button>
                    )}
                    {canDelete && (
                        <button
                            onClick={() => onDelete(c)}
                            className="px-3 py-1.5 border border-red-500 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-50 transition-colors"
                        >
                            Elimina
                        </button>
                    )}
                </div>
            )
        });
    }

    return columns;
};

const ClientsList = ({
    handleEditClient,
    handleDeleteClient,
    handleAddClient,
    handleViewClient,
    user,
    canEdit = true,
    canDelete = true,
    canAdd = true,
    canView = false,
    reloadKey = 0
}) => {
    const showActions = canEdit || canDelete || canView;
    const [sourceFilter, setSourceFilter] = useState('all');

    const isAdmin = user?.role === 'admin';
    const hasLinkedAgents = useMemo(() => {
        const linked = user?.agent_data?.linked_agents;
        if (!linked) return false;
        try {
            const arr = typeof linked === 'string' ? JSON.parse(linked) : linked;
            return Array.isArray(arr) && arr.length > 0;
        } catch { return false; }
    }, [user]);
    const isFunzionario = !isAdmin && !user?.agent_data?.AreaManager;
    const showSourceFilter = !isAdmin && (hasLinkedAgents || isFunzionario);

    const userRole = user?.role || 'guest';
    const userSalesperson = user?.agent_data?.Salesperson || user?.Salesperson || '';

    const columns = getClientColumns({
        showActions,
        canView,
        canEdit,
        canDelete,
        onView: handleViewClient,
        onEdit: handleEditClient,
        onDelete: handleDeleteClient
    });

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">Elenco Clienti</h2>
                {canAdd && (
                    <Button
                        onClick={handleAddClient}
                        color="primary"
                        className="flex items-center"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Nuovo Cliente
                    </Button>
                )}
            </div>

            {showSourceFilter && (
                <div className="mb-4 flex flex-wrap gap-2">
                    <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm">
                        <button
                            type="button"
                            onClick={() => setSourceFilter('all')}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${sourceFilter === 'all' ? 'bg-red-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                        >
                            Tutti
                        </button>
                        <button
                            type="button"
                            onClick={() => setSourceFilter('mine')}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${sourceFilter === 'mine' ? 'bg-red-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                        >
                            Miei
                        </button>
                        <button
                            type="button"
                            onClick={() => setSourceFilter('linked')}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 ${sourceFilter === 'linked' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            {isFunzionario ? 'Sottoposti & Collegati' : 'Collegati'}
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <DataGrid
                    columns={columns}
                    rowKey="CustSupp"
                    server={{
                        url: CLIENTS_URL,
                        reloadKey: `${userRole}|${userSalesperson}|${sourceFilter}|${reloadKey}`,
                        extraData: () => ({ userRole, userSalesperson, sourceFilter })
                    }}
                    searchPlaceholder="Cerca codice, ragione sociale, P.IVA, agente..."
                    initialSort={CLIENTS_INITIAL_SORT}
                />
            </div>
        </div>
    );
};

export default ClientsList;
